import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganizationData, createOrganizationS3, generateOrganizationId, deleteOrganizationS3, clearOrganizationCache } from '../services/organizationS3Service';
import { AuthenticatedUserContext } from '../auth';
import './HomePage.css';

interface OrgCard {
  id: string;
  name: string;
  portfolioCount: number;
  directorCount: number;
  charter?: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const authenticatedUser = useContext(AuthenticatedUserContext);
  const [organizations, setOrganizations] = useState<OrgCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<OrgCard | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const orgData = await getOrganizationData(false);
        if (orgData && orgData.organizations) {
          setOrganizations(orgData.organizations.map((org: any) => ({
            id: org.id,
            name: org.name,
            portfolioCount: org.portfolios?.length || 0,
            directorCount: org.directors?.length || 0,
            charter: org.charter,
          })));
        }
      } catch (err) {
        console.error('Failed to load organizations:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOrgs();
  }, []);

  const handleCreateOrg = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isCreating) return;
    setIsCreating(true);
    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    if (!name.trim()) { setIsCreating(false); return; }

    try {
      const newOrg: any = {
        id: generateOrganizationId(name),
        name,
        directors: [],
      };
      await createOrganizationS3(newOrg);
      setShowCreateModal(false);
      // Navigate to org settings so user can add portfolios and configure
      navigate(`/org/${newOrg.id}/settings`);
    } catch (err) {
      console.error('Failed to create organization:', err);
      alert('Failed to create organization. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="home-page">
        <div className="home-header">
          <div className="home-logo">🐴 HeyFilly</div>
          <div className="home-actions">
            <a href="https://form.asana.com/?k=COodoRCQXmFrpLIANjTfiQ&d=8442528107068" target="_blank" rel="noopener noreferrer" className="home-btn home-btn-secondary">Support</a>
          </div>
        </div>
        <div className="home-loading">
          <div className="home-spinner"></div>
          <p>Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Header */}
      <div className="home-header">
        <div className="home-logo">🐴 HeyFilly</div>
        <div className="home-actions">
          <button className="home-btn home-btn-secondary" onClick={() => navigate('/onboarding')}>📖 Onboarding</button>
          <a href="https://form.asana.com/?k=COodoRCQXmFrpLIANjTfiQ&d=8442528107068" target="_blank" rel="noopener noreferrer" className="home-btn home-btn-secondary">Support</a>
          <button className="home-btn home-btn-primary" onClick={() => setShowCreateModal(true)}>+ New Org</button>
        </div>
      </div>

      {/* Welcome */}
      <div className="home-welcome">
        <h1>Welcome{authenticatedUser?.email ? `, ${authenticatedUser.email.split('@')[0]}` : ''}!</h1>
        <p>Select an organization to get started.</p>
      </div>

      {/* Org Cards Grid */}
      <div className="home-grid">
        {organizations.map(org => (
          <div key={org.id} className="home-card" onClick={() => navigate(`/org/${org.id}`)}>
            <div className="home-card-header">
              <h3>{org.name}</h3>
            </div>
            {org.charter && (
              <p className="home-card-charter">{org.charter.substring(0, 100)}{org.charter.length > 100 ? '...' : ''}</p>
            )}
            <div className="home-card-stats">
              <div className="home-card-stat">
                <span className="home-card-stat-icon">📁</span>
                <span>{org.portfolioCount} Portfolio{org.portfolioCount !== 1 ? 's' : ''}</span>
              </div>
              <div className="home-card-stat">
                <span className="home-card-stat-icon">👥</span>
                <span>{org.directorCount} Director{org.directorCount !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="home-card-footer">
              <div className="home-card-action">
                Enter →
              </div>
              <button
                className="home-card-delete"
                onClick={(e) => { e.stopPropagation(); setOrgToDelete(org); }}
                title="Delete organization"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}

        {/* Create New Org Card */}
        <div className="home-card home-card-create" onClick={() => setShowCreateModal(true)}>
          <div className="home-card-create-icon">+</div>
          <p>Create New Organization</p>
        </div>
      </div>

      {/* Empty State */}
      {organizations.length === 0 && (
        <div className="home-empty">
          <h2>No organizations yet</h2>
          <p>Create your first organization to start using HeyFilly.</p>
          <button className="home-btn home-btn-primary home-btn-large" onClick={() => setShowCreateModal(true)}>+ Create Organization</button>
        </div>
      )}

      {/* Delete Org Confirmation Modal */}
      {orgToDelete && (
        <div className="home-modal-overlay" onClick={() => setOrgToDelete(null)}>
          <div className="home-modal" onClick={e => e.stopPropagation()}>
            <div className="home-modal-header">
              <h3>Delete Organization</h3>
              <button className="home-modal-close" onClick={() => setOrgToDelete(null)}>×</button>
            </div>
            <p style={{ color: '#4a5568', marginBottom: '8px' }}>
              Are you sure you want to delete <strong>"{orgToDelete.name}"</strong>?
            </p>
            <p style={{ color: '#e53e3e', fontSize: '13px', marginBottom: '16px' }}>
              This action cannot be undone and will permanently remove all organization data.
            </p>
            <div className="home-modal-actions">
              <button type="button" className="home-btn home-btn-secondary" onClick={() => setOrgToDelete(null)}>Cancel</button>
              <button
                type="button"
                className="home-btn"
                style={{ background: '#e53e3e', color: 'white' }}
                disabled={isDeleting}
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    await deleteOrganizationS3(orgToDelete.id);
                    clearOrganizationCache();
                    setOrganizations(prev => prev.filter(o => o.id !== orgToDelete.id));
                    setOrgToDelete(null);
                  } catch (err) {
                    console.error('Failed to delete org:', err);
                    alert('Failed to delete organization.');
                  } finally {
                    setIsDeleting(false);
                  }
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Org Modal */}
      {showCreateModal && (
        <div className="home-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="home-modal" onClick={e => e.stopPropagation()}>
            <div className="home-modal-header">
              <h3>Create Organization</h3>
              <button className="home-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateOrg}>
              <div className="home-form-group">
                <label>Organization Name</label>
                <input type="text" name="name" required placeholder="e.g., AFT Outbound" autoFocus />
              </div>
              <div className="home-modal-actions">
                <button type="button" className="home-btn home-btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="home-btn home-btn-primary" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;